import React from 'react';
import Table from 'react-bootstrap/Table';
import { Link } from 'react-router-dom';
import * as ROUTES from '../../../lib/routing/frontend';
import { invoiceAmountFormatter, niceDateFormatter } from '../../../lib/helper/formatting';
import { invoiceStatus } from '../../../lib/helper/invoiceStatus';
import { INVOICES_ALL, INVOICES_UNPAID, filterPatientInvoices, pageCountFor, pageOfInvoices } from '../../../lib/helper/patientInvoices';
import Paginator from '../../../components/common/Paginator';

/**
 * MED-921 - every invoice for this person, across every consultant.
 *
 * Across every consultant is the point of the register. The same human billed by three
 * consultants was three unconnected sets of rows before MED-919; this is the one screen that puts
 * them together.
 *
 * The invoices arrive embedded in the patient response rather than fetched separately, because
 * `/invoices` is scoped by InvoiceForCurrentUserByRoleExtension - for a staff user it lists only
 * the invoices assigned to them, which is a work queue and not this list.
 *
 * **Unpaid is the default view.** Of 94,489 linked invoices, 1,381 are still owed - so opening on
 * everything would put the handful that need action behind ninety-odd thousand that do not. All
 * is one click away, and the toggle says how many are in each.
 *
 * Filtering and paging happen here rather than server-side because the whole list is already
 * present and small: mean 1.64 invoices per patient, busiest 117. See patientInvoices.js.
 *
 * Laid out as a `crud__section` to match the rest of the record, the way ConsultantSingle builds
 * its tabs out of the same blocks.
 */
class PatientInvoicesCard extends React.Component {
  constructor(props) {
    super(props);

    this.state = { mode: INVOICES_UNPAID, page: 1 };
  }

  setMode(mode) {
    // Back to page one on every switch - the page you were on in All rarely exists in Unpaid.
    this.setState({ mode, page: 1 });
  }

  changePage(page) {
    this.setState({ page });
  }

  render() {
    const { patient } = this.props;
    const all = patient.invoices || [];
    const unpaid = filterPatientInvoices(all, INVOICES_UNPAID);

    const shown = this.state.mode === INVOICES_ALL ? all : unpaid;
    const pageCount = pageCountFor(shown);
    const rows = pageOfInvoices(shown, this.state.page);

    return (
      <div className="crud__section mb-3 bg-white">
        <div className="crud__section-icon">
          <i className="fas fa-file-invoice" />
        </div>
        <div className="d-flex justify-content-between align-items-start flex-wrap">
          <div>
            <h2 className="crud__section-header">Invoices</h2>
            <p className="text-muted mb-3">Every invoice for this person, across every consultant.</p>
          </div>
          <div className="btn-group btn-group-sm mb-3" role="group" aria-label="Which invoices to show">
            <button
              type="button"
              className={`btn ${this.state.mode === INVOICES_UNPAID ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => this.setMode(INVOICES_UNPAID)}>
              Unpaid ({unpaid.length})
            </button>
            <button
              type="button"
              className={`btn ${this.state.mode === INVOICES_ALL ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => this.setMode(INVOICES_ALL)}>
              All ({patient.invoiceCount})
            </button>
          </div>
        </div>

        {rows.length === 0 && (
          <p className="text-muted mb-0">
            {this.state.mode === INVOICES_UNPAID && all.length > 0
              ? 'Nothing outstanding for this patient. Void and written-off invoices are not counted as unpaid.'
              : 'No invoices are linked to this patient.'}
          </p>
        )}

        {rows.length > 0 && (
          <Table striped hover responsive size="sm" className="mb-0">
            <thead>
              <tr>
                <th>Invoice No</th>
                <th>Date</th>
                <th>Consultant</th>
                <th className="text-right">Amount</th>
                <th className="text-right">Outstanding</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((invoice) => {
                const status = invoiceStatus(invoice);

                return (
                  <tr key={invoice.id}>
                    <td>
                      {/* Same affordance as the invoice list, so an invoice number looks the
                          same wherever it appears. */}
                      <Link to={ROUTES.INVOICES.SINGLE.replace(':id', invoice.id)} className="btn btn-sm btn-outline-dark">
                        {invoice.invoiceNo}
                      </Link>
                    </td>
                    <td>{invoice.invoiceDate ? niceDateFormatter(invoice.invoiceDate) : <span className="text-muted">-</span>}</td>
                    <td>{invoice.consultant ? invoice.consultant.name : <span className="text-muted">-</span>}</td>
                    <td className="text-right">{invoiceAmountFormatter(invoice.invoiceAmount)}</td>
                    <td className="text-right">{invoiceAmountFormatter(invoice.outstandingBalance)}</td>
                    <td>
                      <span className={`badge ${status.className}`}>{status.label}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}

        {pageCount > 1 && (
          <Paginator
            currentPage={this.state.page}
            pageCount={pageCount}
            setPage={this.changePage.bind(this)}
            nextPage={() => this.changePage(Math.min(this.state.page + 1, pageCount))}
            prevPage={() => this.changePage(Math.max(this.state.page - 1, 1))}
          />
        )}
      </div>
    );
  }
}

export default PatientInvoicesCard;
